// Aspecto del editor dentro del iframe (mismo origen: el motor se sirve en /office/engine/ de este dominio).
// Solo cambia colores, tipografía y pestañas visibles; el botón "Acerca de" del motor lo controla la licencia.

// O14 (auditoría 2026-10-09): Inter sale del propio dominio (public/fuentes/), no de Google en cada apertura.
// Son los mismos archivos variables que servía fonts.gstatic.com, con sus mismos rangos.
const FONT_CSS = `
  @font-face {
    font-family: 'Inter'; font-style: normal; font-weight: 400 600; font-display: swap;
    src: url('/fuentes/inter-latin-ext.woff2') format('woff2');
    unicode-range: U+0100-02BA, U+02BD-02C5, U+02C7-02CC, U+02CE-02D7, U+02DD-02FF, U+0304, U+0308, U+0329, U+1D00-1DBF, U+1E00-1E9F, U+1EF2-1EFF, U+2020, U+20A0-20AB, U+20AD-20C4, U+2113, U+2C60-2C7F, U+A720-A7FF;
  }
  @font-face {
    font-family: 'Inter'; font-style: normal; font-weight: 400 600; font-display: swap;
    src: url('/fuentes/inter-latin.woff2') format('woff2');
    unicode-range: U+0000-00FF, U+0131, U+0152-0153, U+02BB-02BC, U+02C6, U+02DA, U+02DC, U+0304, U+0308, U+0329, U+2000-206F, U+20AC, U+2122, U+2191, U+2193, U+2212, U+2215, U+FEFF, U+FFFD;
  }
`;

// Temas del motor que usa la interfaz: los dos "modernos" comparten diseño e íconos, así que alternar
// solo cambia colores. La paleta Orvian se aplica encima de cualquiera de su tipo (claro u oscuro), por si
// alguien elige otro tema desde Archivo → Configuración avanzada.
export const EDITOR_THEME = { light: 'theme-white', dark: 'theme-night' };
const LIGHT_THEMES = ['theme-white', 'theme-light', 'theme-classic-light', 'theme-gray'];
const DARK_THEMES = ['theme-night', 'theme-dark', 'theme-contrast-dark'];

const BASE_CSS = `
  body, .toolbar, .statusbar, #left-menu, #right-menu, .ribtab a, .btn-toolbar, .dropdown-menu,
  .combobox input, .form-control, .asc-window, .tooltip, .input-field input {
    font-family: 'Inter', 'Segoe UI', Roboto, Arial, sans-serif !important;
  }
  .toolbar .box-tabs .ribtab > a { font-size: 14px !important; letter-spacing: 0; }
  /* Pestañas que la interfaz propia reemplaza: marketplace de extensiones y el asistente IA del motor (id tipo GUID).
     Solo CSS: el editor crea y vuelve a mostrar esas pestañas después de cargar */
  .toolbar .box-tabs .ribtab:has(a[data-tab="plugins"]),
  .toolbar .box-tabs .ribtab:has(a[data-tab*="-"]) { display: none !important; }
  /* El encabezado propio ya muestra marca y avatar: sin logo ni avatar repetido en la barra */
  .toolbar .box-tabs .extra.left section.logo, #header-logo { display: none !important; }
  .toolbar .box-tabs .hedset:has(.btn-current-user), .toolbar .btn-current-user { display: none !important; }
  .toolbar .box-tabs .extra.left { min-width: 56px; }
  /* Botón ⓘ "Acerca de" del menú izquierdo: solo se oculta (About.js del motor queda intacto; quitar esta regla lo devuelve) */
  #left-btn-about { display: none !important; }
`;

// Tokens Orvian (los mismos de index.css) traducidos a las variables del motor. Van en variables y no en
// reglas sueltas porque el motor lee estos colores al aplicar el tema para pintar el lienzo (reglas,
// encabezados de celdas, barras de desplazamiento), que es un <canvas> y no obedece al CSS.
const PALETTE = {
  light: {
    canvas: '#eef0f7', panel: '#f7f8fc', surface: '#ffffff', pill: '#eceef6',
    hover: '#e6e8f2', pressed: '#e1ebfd', pressedHover: '#d2e1fc',
    line: '#e6e8f1', line2: '#d6d9e6', line3: '#c3c7d6',
    text: '#1a1b22', text2: '#575b6b', text3: '#8d92a4', icon: '#474a58',
    accent: '#2563eb', accentHover: '#1d4ed8', accentPressed: '#1e40af', accentText: '#1d4ed8', accentSoftLine: '#93c5fd',
    link: '#2563eb', linkHover: '#1d4ed8', shadow: 'rgba(20, 22, 45, .08)',
  },
  dark: {
    canvas: '#0e1019', panel: '#161826', surface: '#1b1e2e', pill: '#1b1e2e',
    hover: '#23273a', pressed: '#1b2a45', pressedHover: '#223454',
    line: '#23263a', line2: '#2e3247', line3: '#40455c',
    text: '#e9e9ed', text2: '#a9adc0', text3: '#7a7f93', icon: '#cfd2de',
    accent: '#3b82f6', accentHover: '#60a5fa', accentPressed: '#2563eb', accentText: '#93c5fd', accentSoftLine: '#60a5fa',
    link: '#93c5fd', linkHover: '#bfdbfe', shadow: 'rgba(0, 0, 0, .35)',
  },
};

function themeCss(themes, p) {
  const b = themes.map((t) => `body.${t}`).join(', ');
  const at = (sel) => themes.map((t) => `body.${t} ${sel}`).join(', ');
  const kinds = ['document', 'spreadsheet', 'presentation', 'pdf', 'visio'];
  const perKind = (name, value) => kinds.map((k) => `--${name}-${k}: ${value} !important;`).join(' ');
  return `
  ${b} {
    ${perKind('toolbar-header', p.panel)}
    ${perKind('text-toolbar-header-on-background', p.panel)}
    ${perKind('highlight-toolbar-tab-underline', p.accent)}
    ${perKind('highlight-header-tab-underline', p.accent)}
    --background-normal: ${p.surface} !important;
    --background-toolbar: ${p.panel} !important;
    --background-toolbar-tab: ${p.panel} !important;
    --background-toolbar-additional: ${p.canvas} !important;
    --background-statusbar: ${p.panel} !important;
    --background-pane: ${p.panel} !important;
    --background-contrast-popover: ${p.surface} !important;
    --background-primary-dialog-button: ${p.accent} !important;
    --background-accent-button: ${p.accent} !important;
    --highlight-primary-dialog-button-hover: ${p.accentHover} !important;
    --highlight-primary-dialog-button-pressed: ${p.accentPressed} !important;
    --highlight-button-hover: ${p.hover} !important;
    --highlight-button-pressed: ${p.pressed} !important;
    --highlight-button-pressed-hover: ${p.pressedHover} !important;
    --highlight-header-button-hover: ${p.hover} !important;
    --highlight-header-button-pressed: ${p.pressed} !important;
    --highlight-category-button-hover: ${p.hover} !important;
    --highlight-category-button-pressed: ${p.pressed} !important;
    --highlight-text-select: ${p.accent} !important;
    --border-toolbar: ${p.line} !important;
    --border-toolbar-active-panel-top: ${p.panel} !important;
    --border-divider: ${p.line} !important;
    --border-regular-control: ${p.line2} !important;
    --border-color-shading: ${p.line2} !important;
    --border-contrast-popover: ${p.line2} !important;
    --border-preview-hover: ${p.accentSoftLine} !important;
    --border-preview-select: ${p.accent} !important;
    --border-control-focus: ${p.accent} !important;
    --border-button-pressed-focus: ${p.accent} !important;
    --border-fill-input-focused: ${p.accent} !important;
    --chb-border-normal-focus: ${p.accent} !important;
    --chb-border-checked-focus: ${p.accent} !important;
    --text-normal: ${p.text} !important;
    --text-normal-pressed: ${p.text} !important;
    --text-secondary: ${p.text2} !important;
    --text-tertiary: ${p.text3} !important;
    --text-toolbar-header: ${p.text} !important;
    --text-link: ${p.link} !important;
    --text-link-hover: ${p.linkHover} !important;
    --text-link-active: ${p.linkHover} !important;
    --text-link-visited: ${p.link} !important;
    --icon-normal: ${p.icon} !important;
    --icon-normal-pressed: ${p.text} !important;
    --icon-toolbar-header: ${p.icon} !important;
    --canvas-background: ${p.canvas} !important;
    --canvas-page-border: ${p.line2} !important;
    --canvas-ruler-background: ${p.surface} !important;
    --canvas-ruler-border: ${p.line} !important;
    --canvas-ruler-margins-background: ${p.line} !important;
    --canvas-ruler-mark: ${p.text2} !important;
    --canvas-ruler-handle-border: ${p.text2} !important;
    --canvas-cell-title-text: ${p.text2} !important;
    --canvas-cell-title-background: ${p.panel} !important;
    --canvas-cell-title-background-hover: ${p.hover} !important;
    --canvas-cell-title-background-selected: ${p.pressed} !important;
    --canvas-cell-title-border: ${p.line2} !important;
    --canvas-cell-title-border-hover: ${p.line3} !important;
    --canvas-cell-title-border-selected: ${p.accentSoftLine} !important;
    --canvas-scroll-thumb: ${p.hover} !important;
    --canvas-scroll-thumb-hover: ${p.line3} !important;
    --canvas-scroll-thumb-pressed: ${p.line3} !important;
    --canvas-scroll-thumb-border: ${p.line2} !important;
    --canvas-scroll-thumb-border-hover: ${p.line3} !important;
    --canvas-scroll-thumb-border-pressed: ${p.line3} !important;
    --canvas-select-all-icon: ${p.text3} !important;
    --shadow-toolbar: 0 1px 2px ${p.shadow} !important;
  }
  ${at('#viewport')}, ${at('#app-title')}, ${at('#toolbar')}, ${at('#toolbar > .toolbar')}, ${at('.toolbar .box-tabs')},
  ${at('#statusbar')}, ${at('.statusbar')}, ${at('#left-menu')}, ${at('#right-menu')}, ${at('.left-menu-full-ct')},
  ${at('.layout-region')}, ${at('#id_main_view')}, ${at('#ws-canvas-outer')} {
    background-color: ${p.panel} !important;
  }
  ${at('#toolbar > .toolbar')}, ${at('.toolbar .box-tabs')} { border: 0 !important; box-shadow: none !important; }
  ${at('.toolbar .box-tabs .ribtab > a')} { color: ${p.text2} !important; }
  ${at('.toolbar .box-tabs .ribtab > a:hover')} { color: ${p.text} !important; background: ${p.hover} !important; border-radius: 8px; }
  ${at('.toolbar .box-tabs .ribtab.active > a')} { color: ${p.accentText} !important; font-weight: 600 !important; }
  ${at('.toolbar .box-controls')} {
    background: ${p.pill} !important;
    border: 1px solid ${p.line} !important;
    box-shadow: none !important;
    border-radius: 12px !important;
  }
  ${at('.toolbar .box-controls .btn-toolbar:not(.disabled):hover')} { background-color: ${p.hover} !important; }
  ${at('#statusbar')}, ${at('.statusbar')} { border-top: 1px solid ${p.line} !important; }
  ${at('#left-menu')}, ${at('#right-menu')} { border-color: transparent !important; }
  /* Esqueleto que el motor pinta mientras abre el archivo */
  ${at('.loadmask')} {
    ${['word', 'cell', 'slide', 'pdf', 'visio'].map((k) => `--sk-background-toolbar-header-${k}: ${p.panel} !important;`).join(' ')}
    --sk-background-toolbar: ${p.panel} !important;
    --sk-background-toolbar-controls: ${p.pill} !important;
    --sk-background-toolbar-tab: ${p.hover} !important;
    --sk-background-toolbar-button: ${p.hover} !important;
    --sk-shadow-toolbar: none !important;
    --sk-canvas-background: ${p.canvas} !important;
    --sk-canvas-content-background: #ffffff !important;
    --sk-canvas-page-border: ${p.line2} !important;
    --sk-canvas-line: rgba(20, 22, 45, .06) !important;
  }
`;
}

/* Rediseño moderno (2026-10-03): Archivo con filas redondeadas y contenido en tarjeta, formatos en
   tarjetas, la cinta como tarjeta blanca, galería de estilos en tarjetas, menús y ventanas redondeados.
   Usa las variables de tema del motor (ya con la paleta Orvian): vale en claro y oscuro.
   MISMO texto que `_moderno` de app/lib/escritorio/piel_editor.dart (programa de escritorio). */
const MODERNO_CSS = `
/* ===== Archivo (Backstage) ===== */
#file-menu-panel { background: var(--background-toolbar) !important; }
#file-menu-panel .panel-menu {
  width: 272px !important; padding: 14px 12px !important; box-sizing: border-box;
  background: var(--background-toolbar) !important; border-right: 1px solid var(--border-toolbar) !important;
}
#file-menu-panel .panel-menu li.fm-btn { margin: 0 0 2px !important; padding: 0 !important; height: auto !important; min-height: 0 !important; }
#file-menu-panel #fm-btn-suggest { display: none !important; }
#file-menu-panel .panel-menu li.fm-btn > a.menu-item:not(:has(.menu-item-icon)) { padding-left: 44px !important; }
#file-menu-panel .panel-menu li.fm-btn > a.menu-item {
  display: flex !important; align-items: center; gap: 12px;
  height: 38px !important; line-height: 38px !important; padding: 0 12px !important;
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
  border-radius: 10px !important; font-size: 14px !important; font-weight: 500 !important;
  color: var(--text-normal) !important; transition: background .15s, color .15s;
}
#file-menu-panel .panel-menu li.fm-btn > a.menu-item .menu-item-icon { margin: 0 !important; position: static !important; flex: 0 0 20px; opacity: .85; }
#file-menu-panel .panel-menu li.fm-btn:not(.disabled) > a.menu-item:hover { background: var(--highlight-button-hover) !important; }
#file-menu-panel .panel-menu li.fm-btn.active > a.menu-item {
  background: var(--highlight-button-pressed) !important; color: var(--text-link) !important; font-weight: 600 !important;
}
#file-menu-panel .panel-menu li.fm-btn.disabled > a.menu-item { opacity: .45; }
#file-menu-panel .panel-menu li#fm-btn-return > a.menu-item {
  border: 1px solid var(--border-regular-control) !important; background: var(--background-normal) !important;
  height: 38px !important; line-height: 38px !important; margin-bottom: 6px;
}
#file-menu-panel .panel-menu li.devider { margin: 10px 12px !important; border-top: 1px solid var(--border-toolbar) !important; height: 0 !important; background: none !important; }

#file-menu-panel .panel-context {
  left: 272px !important; background: var(--canvas-background) !important; padding: 24px !important; box-sizing: border-box;
}
#file-menu-panel .panel-context > .content-box {
  background: var(--background-normal) !important; border: 1px solid var(--border-toolbar) !important;
  border-radius: 18px !important; box-shadow: 0 1px 2px rgba(20,22,45,.04), 0 12px 32px rgba(20,22,45,.06) !important;
  padding: 28px 32px !important; box-sizing: border-box; max-width: 1180px; height: auto !important; max-height: calc(100% - 0px);
}
#file-menu-panel .content-box .header, #file-menu-panel #id-fms-lbl-protect-header, #file-menu-panel .content-box label.header {
  font-size: 22px !important; font-weight: 600 !important; letter-spacing: -.01em; color: var(--text-normal) !important;
  margin: 0 0 22px !important; padding: 0 !important; line-height: 1.3 !important;
}

/* Descargar como / Guardar copia: formatos en tarjetas */
#file-menu-panel .format-items { display: flex; flex-wrap: wrap; gap: 14px; }
#file-menu-panel .format-items .divider { flex-basis: 100%; height: 0 !important; margin: 4px 0 !important; border: 0 !important; }
#file-menu-panel .format-item { float: none !important; margin: 0 !important; }
#file-menu-panel .btn-doc-format {
  width: 112px !important; height: 128px !important; display: grid !important; place-items: center;
  border-radius: 14px !important; border: 1px solid var(--border-toolbar) !important; background: var(--background-normal) !important;
  transition: transform .15s, box-shadow .15s, border-color .15s; cursor: pointer;
}
#file-menu-panel .btn-doc-format > div { transform: scale(.82); }
#file-menu-panel .btn-doc-format:hover {
  border-color: var(--border-preview-hover) !important; transform: translateY(-2px);
  box-shadow: 0 10px 24px rgba(20,22,45,.10) !important; background: var(--background-normal) !important;
}

/* Información, derechos y configuración: tablas aireadas */
#file-menu-panel #panel-info table.main td, #file-menu-panel #panel-rights table.main td,
#file-menu-panel #panel-settings table td { padding-top: 7px !important; padding-bottom: 7px !important; font-size: 13.5px !important; }
#file-menu-panel #panel-info td.left label, #file-menu-panel #panel-rights td.left label, #file-menu-panel #panel-settings td.left label {
  color: var(--text-secondary) !important; font-size: 13px !important;
}
#file-menu-panel #panel-info tbody > tr.header td, #file-menu-panel #panel-settings tr.header td,
#file-menu-panel #panel-settings table .header {
  font-size: 15px !important; font-weight: 600 !important; padding-top: 18px !important; color: var(--text-normal) !important;
}
#file-menu-panel .content-box input.form-control, #file-menu-panel .content-box .combobox input, #file-menu-panel .content-box textarea {
  border-radius: 8px !important; min-height: 30px;
}
#file-menu-panel .content-box .btn.normal, #file-menu-panel .content-box button.btn {
  border-radius: 9px !important; min-height: 32px; padding: 0 14px !important; font-weight: 500;
}
#file-menu-panel .content-box .btn.primary, #file-menu-panel .content-box button.primary {
  background: var(--background-accent-button) !important; border-color: var(--background-accent-button) !important; color: #fff !important;
}

/* Recientes: filas con hover */
#file-menu-panel #id-recent-view .item, #file-menu-panel #id-recent-view .recent-wrap {
  border-radius: 10px !important; padding: 8px 10px !important;
}
#file-menu-panel #id-recent-view .item:hover { background: var(--highlight-button-hover) !important; }

/* ===== Cintas (Inicio, Insertar, Diseño, Referencias, Colaboración, Protección, Vista) ===== */
.toolbar .box-controls .panel .group { padding: 0 10px !important; }
.toolbar .box-controls .panel .separator.long { border-color: var(--border-toolbar) !important; opacity: .9; margin: 4px 2px !important; }
.toolbar .btn-toolbar { border-radius: 8px !important; }
.toolbar .btn-toolbar.x-huge { border-radius: 10px !important; }
.toolbar .btn-toolbar.x-huge .caption { font-size: 12px !important; font-weight: 500 !important; }
.toolbar .btn-toolbar.active, .toolbar .btn-toolbar:active:not(.disabled), .toolbar .btn-group.open > .btn-toolbar {
  background-color: var(--highlight-button-pressed) !important;
}
.toolbar .btn-group > .btn-toolbar.dropdown-toggle { border-radius: 0 8px 8px 0 !important; }
.toolbar .btn-group.split > .btn-toolbar:first-child { border-radius: 8px 0 0 8px !important; }
.toolbar .combobox .form-control, .toolbar .input-field input { border-radius: 8px !important; }
.toolbar .combo-dataview, .toolbar .combo-styles { border-radius: 10px !important; overflow: hidden; }
.toolbar .combo-dataview .item, .toolbar .combo-styles .item { border-radius: 8px !important; }

/* Menús desplegables, listas y tooltips */
.dropdown-menu {
  border-radius: 12px !important; padding: 6px !important; border: 1px solid var(--border-contrast-popover) !important;
  box-shadow: 0 2px 6px rgba(20,22,45,.06), 0 18px 40px rgba(20,22,45,.14) !important;
}
.dropdown-menu > li > a { border-radius: 8px !important; padding-top: 6px !important; padding-bottom: 6px !important; font-size: 13px !important; }
.dropdown-menu > li > a:hover, .dropdown-menu > li > a:focus { background-color: var(--highlight-button-hover) !important; }
.dropdown-menu .divider { margin: 5px 8px !important; }
.tooltip .tooltip-inner { border-radius: 8px !important; padding: 6px 10px !important; font-size: 12px !important; }

/* Ventanas del editor (Párrafo, Tabla, Imagen…) */
.asc-window { border-radius: 14px !important; overflow: hidden; box-shadow: 0 24px 60px rgba(20,22,45,.22) !important; }
.asc-window > .header { border-radius: 14px 14px 0 0 !important; font-weight: 600 !important; }
.asc-window .footer .dlg-btn, .asc-window .btn.normal { border-radius: 9px !important; }
.asc-window .dlg-btn.primary, .asc-window .btn.primary { background: var(--background-accent-button) !important; border-color: var(--background-accent-button) !important; }
.asc-window input.form-control, .asc-window .combobox input { border-radius: 8px !important; }
html body[class] #file-menu-panel .content-box td.header { font-size: 22px !important; font-weight: 600 !important; letter-spacing: -.01em; padding-bottom: 10px !important; color: var(--text-normal) !important; }
#file-menu-panel #panel-settings .flex-settings > .header { font-size: 22px !important; font-weight: 600 !important; }

/* Galería de estilos (Normal, Título 1…): tarjetas separadas */
#slot-field-styles .field-picker, .toolbar .combo-dataview .dataview.inner { background: transparent !important; border: 0 !important; }
#slot-field-styles .field-picker .item {
  border: 1px solid var(--border-toolbar) !important; border-radius: 9px !important; margin: 0 3px !important;
  background: var(--background-normal) !important; box-shadow: none !important; overflow: hidden; transition: border-color .15s, box-shadow .15s;
}
#slot-field-styles .field-picker .item:hover { border-color: var(--border-preview-hover) !important; box-shadow: 0 4px 12px rgba(20,22,45,.08) !important; }
#slot-field-styles .field-picker .item.selected { border-color: var(--border-preview-select) !important; box-shadow: 0 0 0 1px var(--border-preview-select) inset !important; }
#slot-field-styles .combo-dataview, #slot-field-styles .combo-styles { border: 0 !important; background: transparent !important; }
#slot-field-styles .more-box, #slot-field-styles .combo-dataview .more-box { border: 0 !important; background: transparent !important; }
#slot-field-styles .more-box button, #slot-field-styles button.more { border-radius: 9px !important; border: 1px solid var(--border-toolbar) !important; background: var(--background-normal) !important; }

/* Cinta como tarjeta: blanca, borde fino y sombra suave (antes: bloque gris plano) */
html body[class] .toolbar .box-controls {
  background: var(--background-normal) !important; border: 1px solid var(--border-toolbar) !important;
  border-radius: 14px !important; box-shadow: 0 1px 2px rgba(20,22,45,.04), 0 6px 18px rgba(20,22,45,.05) !important;
  margin: 0 10px 8px !important;
}
body.theme-type-dark .toolbar .box-controls { box-shadow: 0 6px 18px rgba(0,0,0,.25) !important; }
.toolbar .box-controls .separator.long { border-left: 1px solid var(--border-toolbar) !important; border-right: 0 !important; height: 52px !important; margin-top: 10px !important; }
.toolbar .btn-toolbar:not(.disabled):hover { background-color: var(--highlight-button-hover) !important; }
.toolbar .spinner input, .toolbar .spinner { border-radius: 8px !important; }
.toolbar .btn-toolbar.x-huge .caption, .toolbar .btn-toolbar .caption { color: var(--text-normal) !important; }
.toolbar .btn-toolbar.disabled .caption { opacity: .55; }

/* Galerías de la cinta en TODOS los editores (estilos de celda, temas, estilos de tabla…): tarjetas */
.toolbar .combo-dataview { border-color: transparent !important; background: transparent !important; }
.toolbar .combo-dataview .dataview, .toolbar .combo-dataview .view { background: transparent !important; border-color: transparent !important; }
.toolbar .combo-dataview .item {
  border: 1px solid var(--border-toolbar) !important; border-radius: 8px !important; margin: 1px 3px !important;
  background: var(--background-normal) !important; overflow: hidden; box-shadow: none !important; transition: border-color .15s, box-shadow .15s;
}
.toolbar .combo-dataview .item:hover { border-color: var(--border-preview-hover) !important; box-shadow: 0 4px 12px rgba(20,22,45,.08) !important; }
.toolbar .combo-dataview .item.selected { border-color: var(--border-preview-select) !important; box-shadow: 0 0 0 1px var(--border-preview-select) inset !important; }
.toolbar .combo-dataview .more-box, .toolbar .combo-dataview .more-container { border: 0 !important; background: transparent !important; }
.toolbar .combo-dataview button.more, .toolbar .combo-dataview .more-box button { border-radius: 8px !important; }

/* Paletas de color: esquinas suaves, anillo azul y títulos de sección */
.theme-colorpalette .palette-color-caption { font-size: 12px !important; font-weight: 600 !important; color: var(--text-secondary) !important; margin: 2px 0 6px !important; }
.theme-colorpalette a em, .theme-colorpalette a em span { border-radius: 4px !important; }
.theme-colorpalette a:hover em, .theme-colorpalette a.selected em { box-shadow: 0 0 0 2px var(--border-preview-select) !important; border-color: transparent !important; }

/* Galerías dentro de menús (formas, gráficos, tablas, numeraciones): celdas redondeadas */
.dropdown-menu .dataview .item, .dropdown-menu .menu-shapes .item, .dropdown-menu .menu-insertchart .item { border-radius: 8px !important; }
.dropdown-menu .dataview .item:hover { background-color: var(--highlight-button-hover) !important; }
.dropdown-menu .dataview .item.selected { background-color: var(--highlight-button-pressed) !important; }
.dropdown-menu .group-description, .dropdown-menu .dataview .grouped-data .group-description {
  font-size: 11px !important; font-weight: 700 !important; text-transform: uppercase; letter-spacing: .06em; color: var(--text-tertiary) !important;
}
.dropdown-menu > li > a .menu-item-icon, .dropdown-menu > li > a svg.menu-item-icon { opacity: .9; }
.dropdown-menu .dropdown-submenu > .dropdown-menu { margin-left: 4px !important; }

/* Hoja en oscuro (2026-10-07): el motor pone blanco el cursor de escritura de la celda; sobre el gris de las
   celdas casi no se ve. Negro, como en claro. */
body.theme-type-dark [id^="ce-cursor"] { background-color: #000 !important; }
`;

const SKIN_CSS = BASE_CSS + themeCss(LIGHT_THEMES, PALETTE.light) + themeCss(DARK_THEMES, PALETTE.dark) + MODERNO_CSS;

/* Modo app (Orvian Email en el teléfono). El motor no tiene interfaz móvil editable sin licencia, así que
   se usa la de escritorio y aquí se le quita lo que en una pantalla estrecha solo estorba. Todo cuelga de
   `html.orvian-app` (lo pone injectSkin) para no tocar nunca el editor del portal. El resto de ajustes
   (sin reglas, zoom al ancho) viajan firmados en la config: /api/office/config?layout=app. */
const APP_CSS = `
  /* Las dos tiras de iconos de los lados: comentarios, buscar e historial están en el menú de la app */
  html.orvian-app #left-menu .tool-menu-btns, html.orvian-app #right-menu { display: none !important; }
  /* Sin tira, el menú izquierdo solo ocupa sitio cuando hay un panel abierto, y entonces a todo el ancho */
  html.orvian-app #left-menu:not(:has(.left-panel > :not([style*="none"]))) { width: 0 !important; }
  html.orvian-app #left-menu:has(.left-panel > :not([style*="none"])) { width: 100% !important; }
  html.orvian-app #left-menu .left-panel { padding-left: 0 !important; }
  html.orvian-app .toolbar .box-tabs .extra.left { min-width: 0 !important; }
  /* Barra de estado: en documentos y presentaciones solo repite datos; en hojas trae las pestañas y se queda */
  html.orvian-app:not(.orvian-app-cell) #statusbar { display: none !important; }
  /* En hojas: el zoom se hace pellizcando y el estado de guardado lo dice la app; queda sitio para las pestañas */
  html.orvian-app.orvian-app-cell #status-zoom-box, html.orvian-app.orvian-app-cell #status-action { display: none !important; }
  /* Imprimir no tiene sentido en el teléfono; el lienzo se desplaza con el dedo */
  html.orvian-app #slot-btn-print, html.orvian-app #slot-hbtn-print, html.orvian-app #slot-hbtn-print-quick { display: none !important; }
  html.orvian-app:not(.orvian-app-cell) #id_vertical_scroll,
  html.orvian-app:not(.orvian-app-cell) #id_panel_hor_scroll { opacity: 0 !important; pointer-events: none !important; }
  /* La barra del motor está pensada para ratón y pantalla ancha: en la app la
     dibuja la app en nativo (ver editorComandos.js). Se puede recuperar entera
     desde "Barra del editor" del menú, que añade la clase orvian-barra. */
  html.orvian-app:not(.orvian-barra) #toolbar { display: none !important; }
  /* Controles pensados para el dedo: pestañas más altas y botones con más aire */
  html.orvian-app .toolbar .box-tabs .ribtab > a { padding: 0 12px !important; }
  html.orvian-app .toolbar .box-controls .btn-toolbar { min-width: 30px !important; }
  html.orvian-app .toolbar .box-controls .group { padding-left: 4px !important; padding-right: 4px !important; }
  html.orvian-app .dropdown-menu li > a { padding-top: 9px !important; padding-bottom: 9px !important; }
  html.orvian-app .asc-window.modal { max-width: calc(100vw - 16px) !important; }
`;

/* Lectura en PC (enlace público sin permiso de edición): la cinta del motor solo trae botones
   deshabilitados (cortar, pegar, deshacer…) y se come ~90 px. Se quita entera; lo útil para leer
   (buscar, comentarios, descargar, tema) está en la cabecera propia, y la barra de estado —páginas y
   zoom— y el menú izquierdo —buscar, índice, miniaturas— se quedan. Cuelga de `html.orvian-lectura`. */
const LECTURA_CSS = `
  html.orvian-lectura #toolbar { display: none !important; }
`;

/* Archivo → Info: «Aplicación» decía ONLYOFFICE/9.4…; se enseña el nombre de la app de Orvian. */
const NOMBRE_APP = { word: 'Orvian Docs', cell: 'Orvian Sheets', slide: 'Orvian Slides', pdf: 'Orvian PDF', diagram: 'Orvian Diagramas' };
function nombreAplicacionCss(tipo) {
  return `#id-info-appname { font-size: 0 !important; } #id-info-appname::after { content: '${NOMBRE_APP[tipo] || 'Orvian Docs'}'; font-size: 13.5px; }`;
}

function editorTypeOf(doc) {
  const path = doc?.location?.pathname || '';
  if (path.includes('/spreadsheeteditor/')) return 'cell';
  if (path.includes('/presentationeditor/')) return 'slide';
  if (path.includes('/pdfeditor/')) return 'pdf';
  if (path.includes('/visioeditor/')) return 'diagram';
  return 'word';
}

/**
 * Inyecta el estilo en cuanto el documento del iframe existe (antes de que el editor pinte). Sirve para los dos temas.
 * `app: true` añade el modo teléfono (ver APP_CSS).
 */
export function injectSkin(iframe, { app = false, lectura = false } = {}) {
  let stopped = false;
  let timer = null;

  const tryInject = () => {
    if (stopped) return;
    let doc = null;
    try {
      doc = iframe.contentDocument;
    } catch {
      return;
    }
    if (!doc || !doc.head || doc.location.href === 'about:blank') {
      timer = setTimeout(tryInject, 30);
      return;
    }
    if (doc.getElementById('office-skin')) return;
    const style = doc.createElement('style');
    style.id = 'office-skin';
    style.textContent = FONT_CSS + SKIN_CSS + (app ? APP_CSS : '') + (lectura ? LECTURA_CSS : '') + nombreAplicacionCss(editorTypeOf(doc));
    doc.head.appendChild(style);
    if (app) doc.documentElement.classList.add('orvian-app', `orvian-app-${editorTypeOf(doc)}`);
    if (lectura) doc.documentElement.classList.add('orvian-lectura');
  };

  tryInject();
  iframe.addEventListener('load', tryInject);
  return () => {
    stopped = true;
    clearTimeout(timer);
    iframe.removeEventListener('load', tryInject);
  };
}

/**
 * Deja escrito el tema con el que debe abrir el editor. El motor lee `ui-theme-id` de localStorage (mismo
 * origen que el portal) ANTES que el `uiTheme` de la config: si alguien lo cambió alguna vez desde el
 * propio editor, sin esto abriría siempre con ese tema aunque el portal esté en el otro.
 */
export function rememberEditorTheme(dark) {
  try {
    const current = localStorage.getItem('ui-theme-id');
    // Si ya hay un tema del mismo tipo elegido desde el editor (p. ej. "Oscuro contraste"), se respeta
    if (current && current !== 'theme-system' && isDarkEditorTheme(current) === dark) return;
    localStorage.setItem('ui-theme-id', dark ? EDITOR_THEME.dark : EDITOR_THEME.light);
  } catch {
    /* sin localStorage: manda el uiTheme de la config */
  }
}

/**
 * Ajustes del modo app que no se pueden pedir en la config: en presentaciones, miniaturas en una tira
 * abajo (a la izquierda se comían un tercio de la pantalla) y sin el panel de notas.
 */
export function applyAppLayout(iframe) {
  try {
    const api = iframe?.contentWindow?.editor;
    if (api && typeof api.asc_SetThumbnailsPosition === 'function') {
      const bottom = iframe.contentWindow.AscCommon?.thumbnailsPositionMap?.bottom ?? 2;
      api.asc_SetThumbnailsPosition(bottom);
      api.asc_ShowNotes?.(false);
    }
    iframe?.contentWindow?.dispatchEvent(new Event('resize'));
  } catch {
    /* el editor aún no está listo: se vuelve a intentar desde quien llama */
  }
}

/** ¿El id de tema del motor es oscuro? (para seguir un cambio hecho desde Configuración avanzada) */
export function isDarkEditorTheme(id) {
  if (id === 'theme-system') return !!window.matchMedia?.('(prefers-color-scheme: dark)').matches;
  return DARK_THEMES.includes(id) || /dark|night/.test(id || '');
}

/**
 * Cambia el tema del editor abierto sin recargarlo (recargar desconectaría de la coedición).
 * Devuelve false si el motor todavía no terminó de iniciar su gestor de temas.
 */
export function applyEditorTheme(iframe, dark) {
  let themes = null;
  try {
    themes = iframe?.contentWindow?.Common?.UI?.Themes;
  } catch {
    return false;
  }
  if (!themes?.api || typeof themes.setTheme !== 'function') return false;
  const current = typeof themes.currentThemeId === 'function' ? themes.currentThemeId() : null;
  const alreadyDark = current ? isDarkEditorTheme(current) : null;
  try {
    if (alreadyDark !== dark) themes.setTheme(dark ? EDITOR_THEME.dark : EDITOR_THEME.light);
    aplicarHojaOscura(themes, dark);
    return true;
  } catch {
    return false;
  }
}

/**
 * La HOJA también (2026-10-03, «como Google Docs»): en oscuro el documento y la diapositiva se pintan
 * oscuros con letra clara («Documento oscuro» del motor); en claro, blancos. La hoja de cálculo, a su manera:
 * `graySheetWhitesInDark`.
 */
function aplicarHojaOscura(themes, dark) {
  if (typeof themes.setContentTheme !== 'function') return;
  const ya = typeof themes.isContentThemeDark === 'function' ? themes.isContentThemeDark() : null;
  if (ya !== dark) themes.setContentTheme(dark ? 'dark' : 'light');
}

const ES_MX_LCID = '2058';
const ES_ES_LCID = '3082';

function withSpanishMexico(fn) {
  if (typeof fn !== 'function' || fn.officeEsMx) return fn;
  const wrapped = function spellcheckGetLanguages(...args) {
    const langs = fn.apply(this, args) || {};
    if (!langs[ES_MX_LCID] && langs[ES_ES_LCID]) langs[ES_MX_LCID] = { ...langs[ES_ES_LCID] };
    return langs;
  };
  wrapped.officeEsMx = true;
  return wrapped;
}

/**
 * El corrector del editor no registra español de México (LCID 2058): lo agrega usando el diccionario es_ES.
 * Se engancha en tiempo de ejecución, antes de que cargue el editor, porque sus archivos están cacheados por el CDN.
 */
export function registerSpanishMexico(iframe) {
  let stopped = false;
  let timer = null;

  const hook = () => {
    if (stopped) return;
    let win = null;
    try {
      win = iframe.contentWindow;
      if (!win || win.location.href === 'about:blank') throw new Error('sin documento');
    } catch {
      timer = setTimeout(hook, 15);
      return;
    }
    const common = win.AscCommon || (win.AscCommon = {});
    const desc = Object.getOwnPropertyDescriptor(common, 'spellcheckGetLanguages');
    if (desc?.get?.officeEsMx) return;
    let current = withSpanishMexico(common.spellcheckGetLanguages);
    const get = () => current;
    get.officeEsMx = true;
    try {
      Object.defineProperty(common, 'spellcheckGetLanguages', {
        configurable: true,
        enumerable: true,
        get,
        set: (fn) => {
          current = withSpanishMexico(fn);
        },
      });
    } catch {
      common.spellcheckGetLanguages = current;
    }
  };

  hook();
  iframe.addEventListener('load', hook);
  return () => {
    stopped = true;
    clearTimeout(timer);
    iframe.removeEventListener('load', hook);
  };
}

/** Fondo de las celdas blancas de la hoja en oscuro: gris medio, para no deslumbrar; la letra negra se lee a ~10:1. */
export const GRIS_HOJA_OSCURA = [176, 180, 189];

/**
 * Corrección de color de la hoja en oscuro: los blancos (fondo de las celdas, bandas casi blancas) pasan a gris y TODO
 * lo demás se queda como está (el negro sigue negro, la letra negra sigue negra). Escala, no aplana: FFFFFF y F3F3F3
 * se siguen distinguiendo.
 */
export function colorHojaOscura(r, g, b) {
  if (Math.min(r, g, b) < 235) return { R: r, G: g, B: b };
  const [gr, gg, gb] = GRIS_HOJA_OSCURA;
  return { R: Math.round((gr * r) / 255), G: Math.round((gg * g) / 255), B: Math.round((gb * b) / 255) };
}

/**
 * La HOJA DE CÁLCULO en oscuro (2026-10-07): las celdas blancas se ven grises y los demás colores, tal cual.
 * El «documento oscuro» del motor pasa relleno, borde y letra de cada celda por la MISMA función
 * (`AscCommon.darkModeCorrectColor2`), que invertía la luminosidad sin saber qué pintaba: en «BASE DE DATOS OPS» el
 * encabezado negro con letra blanca quedaba blanco sobre blanco y los rellenos claros, con letra blanca encima. Esa
 * función se sustituye por `colorHojaOscura` (un `000000` puede ser relleno o letra: por eso el negro no se toca).
 * Se engancha antes de que cargue el motor, como `registerSpanishMexico`. Los documentos y las diapositivas siguen con
 * su modo oscuro normal (otro iframe, otro `AscCommon`).
 * MISMA lógica que `puente_js.dart` del programa de escritorio: cambiar los dos.
 */
export function graySheetWhitesInDark(iframe) {
  let stopped = false;
  let timer = null;

  const hook = () => {
    if (stopped) return;
    let win = null;
    try {
      win = iframe.contentWindow;
      if (!win || win.location.href === 'about:blank') throw new Error('sin documento');
    } catch {
      timer = setTimeout(hook, 15);
      return;
    }
    if (!win.location.pathname.includes('/spreadsheeteditor/')) return;
    const common = win.AscCommon || (win.AscCommon = {});
    const desc = Object.getOwnPropertyDescriptor(common, 'darkModeCorrectColor2');
    if (desc?.get?.orvianHoja) return;
    const get = () => colorHojaOscura;
    get.orvianHoja = true;
    try {
      // El motor la define al cargar: se ignora la suya y se queda la nuestra.
      Object.defineProperty(common, 'darkModeCorrectColor2', { configurable: true, enumerable: true, get, set: () => {} });
    } catch {
      common.darkModeCorrectColor2 = colorHojaOscura;
    }
  };

  hook();
  iframe.addEventListener('load', hook);
  return () => {
    stopped = true;
    clearTimeout(timer);
    iframe.removeEventListener('load', hook);
  };
}

/** El editor agrega el nombre del motor al título de su documento (se usa al imprimir): se deja solo el nombre del archivo. */
export function keepEditorTitle(iframe, name) {
  const doc = iframe?.contentDocument;
  const titleEl = doc?.querySelector('title');
  if (!doc || !titleEl || !name) return () => {};
  // `document.title` se lee con los espacios colapsados y recortados. Comparar con el nombre tal cual
  // («BASE DE DATOS OPS  (5).xlsx», con dos espacios) nunca daba igual: el observador reescribía el
  // título sin fin y colgaba la pestaña al 75 % de la carga (2026-10-07). Se compara ya normalizado.
  const title = name.replace(/[\t\n\f\r ]+/g, ' ').trim();
  if (!title) return () => {};
  let observer = null;
  const apply = () => {
    if (doc.title === title) return;
    doc.title = title;
    // Red de seguridad: si el navegador lo normaliza de otra forma, se deja de vigilar antes que entrar en bucle.
    if (doc.title !== title) observer?.disconnect();
  };
  apply();
  if (doc.title !== title) return () => {};
  observer = new MutationObserver(apply);
  observer.observe(titleEl, { childList: true, characterData: true, subtree: true });
  return () => observer.disconnect();
}

/**
 * Hace clic en un control del editor (comentarios, presentación, pestañas…).
 * `hidden: true` vale también para controles ocultos a propósito (la tira lateral en el modo app).
 */
export function clickInEditor(iframe, selectors, { hidden = false } = {}) {
  const doc = iframe?.contentDocument;
  if (!doc) return false;
  for (const selector of [].concat(selectors)) {
    const el = doc.querySelector(selector);
    if (el && (hidden || el.offsetParent !== null)) {
      el.click();
      return true;
    }
  }
  return false;
}
